import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import React, { useMemo, useRef } from 'react';
import { Upload } from '../../../Upload';
import { ImagePanel } from '../MaterialPanel/ImagePanel/ImagePanel';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { useRefCallback, useUncontrolled } from '@tencent/glue-react-use';
import { UploadStatus } from '../MaterialUpload/type/interface';
import { SingleImageUploadGroupContext } from './context/SingleImageUploadGroupContext';
import { useSingleImageUploadGroupContext } from './hooks/useSingleImageUploadGroupContext';
import { usePrefixCls } from '../../../../config/hooks/usePrefixCls';
var mergeListValue = function mergeListValue(value, count) {
  return Array.from({
    length: count
  }, function (item, index) {
    return item !== null && item !== void 0 ? item : value[index];
  });
};
var SortableItem = SortableElement(function (_ref) {
  var item = _ref.item,
    itemIdx = _ref.itemIdx;
  var _useSingleImageUpload = useSingleImageUploadGroupContext(),
    isRemovable = _useSingleImageUpload.isRemovable,
    uploadImage = _useSingleImageUpload.uploadImage,
    onRemove = _useSingleImageUpload.onRemove,
    activeIdx = _useSingleImageUpload.activeIdx,
    onActiveIdxChange = _useSingleImageUpload.onActiveIdxChange;
  var cls = usePrefixCls('single-image-upload-group');
  var uploadHandle = useRefCallback(function () {
    uploadImage(itemIdx);
  });
  var removeHandle = useRefCallback(function () {
    onRemove(itemIdx);
  });
  var itemActive = useRefCallback(function () {
    onActiveIdxChange(itemIdx);
  });
  var cardCls = useMemo(function () {
    return activeIdx === itemIdx ? "".concat(cls, "-card-item ").concat(cls, "-card-item-active") : '';
  }, [activeIdx, itemIdx]);
  var cardContent = React.createElement(Upload.Card, {
    className: cardCls,
    onClick: uploadHandle
  });
  if (item) {
    cardContent = React.createElement(Upload.Card, {
      className: cardCls,
      status: UploadStatus.SUCCESS,
      value: item.imageUrl,
      isReplaceable: true,
      isRemovable: isRemovable,
      onClick: uploadHandle,
      onReplace: uploadHandle,
      onRemove: removeHandle
    });
  }
  return React.createElement("div", {
    className: "".concat(cls, "-image-item"),
    onClick: itemActive
  }, cardContent);
});
var SortableList = SortableContainer(function (_ref2) {
  var items = _ref2.items,
    className = _ref2.className;
  var _useSingleImageUpload2 = useSingleImageUploadGroupContext(),
    triggerMode = _useSingleImageUpload2.triggerMode,
    uploadImage = _useSingleImageUpload2.uploadImage,
    value = _useSingleImageUpload2.value,
    count = _useSingleImageUpload2.count;
  var cls = usePrefixCls('single-image-upload-group');
  var appendTrigger = triggerMode === 'append' && value.length < count && React.createElement(Upload.Card, {
    className: "".concat(cls, "-card-item"),
    onClick: function onClick() {
      return uploadImage(value.length);
    }
  });
  return React.createElement("div", {
    className: className
  }, items.map(function (item, index) {
    return React.createElement(SortableItem, {
      key: "item-".concat(index),
      index: index,
      itemIdx: index,
      item: item
    });
  }), appendTrigger);
});
export var SingleImageUploadGroup = function SingleImageUploadGroup(props) {
  var count = props.count,
    value = props.value,
    onValueChange = props.onValueChange,
    cropSizeConfig = props.cropSizeConfig,
    isRemovable = props.isRemovable,
    activeIdx = props.activeIdx,
    onActiveIdxChange = props.onActiveIdxChange,
    isLockFirstImageSize = props.isLockFirstImageSize,
    apiConfig = props.apiConfig,
    _props$triggerMode = props.triggerMode,
    triggerMode = _props$triggerMode === void 0 ? 'all' : _props$triggerMode;
  var ref = useRef(null);
  var cls = usePrefixCls('single-image-upload-group');
  var list = useMemo(function () {
    if (triggerMode === 'all') {
      return mergeListValue(value, count);
    }
    return value.filter(Boolean);
  }, [value, count, triggerMode]);
  var validCropSize = useMemo(function () {
    if (isLockFirstImageSize) {
      var imgValueIdx = -1;
      value.some(function (item, idx) {
        if (item) {
          imgValueIdx = idx;
          return true;
        }
        return false;
      });
      if (imgValueIdx > -1) {
        var validValueItem = value[imgValueIdx];
        if (!validValueItem) {
          return cropSizeConfig;
        }
        var imageWidth = validValueItem.imageWidth,
          imageHeight = validValueItem.imageHeight;
        return [{
          minWidth: imageWidth,
          maxWidth: imageWidth,
          minHeight: imageHeight,
          maxHeight: imageHeight
        }];
      }
      return cropSizeConfig;
    }
    return cropSizeConfig;
  }, [cropSizeConfig, isLockFirstImageSize, value]);
  var onSortEnd = useRefCallback(function (_ref3) {
    var oldIndex = _ref3.oldIndex,
      newIndex = _ref3.newIndex;
    var nextList = _toConsumableArray(list);
    var old = nextList.splice(oldIndex, 1)[0];
    nextList.splice(newIndex, 0, old);
    onValueChange(nextList);
    onActiveIdxChange === null || onActiveIdxChange === void 0 ? void 0 : onActiveIdxChange(newIndex);
  });
  var uploadImage = useRefCallback(function (idx) {
    var _a;
    (_a = ref.current) === null || _a === void 0 ? void 0 : _a.uploadImage(idx);
  });
  var removeHandle = useRefCallback(function (idx) {
    if (idx < 0 || idx >= value.length) {
      console.error('idx not correct！');
      return;
    }
    var nextValue = value.slice(0, idx);
    if (triggerMode === 'all') {
      nextValue = nextValue.concat(null, value.slice(idx + 1));
    } else {
      nextValue = nextValue.concat(value.slice(idx + 1));
    }
    onValueChange(nextValue);
  });
  var _useUncontrolled = useUncontrolled(activeIdx != null, -1, activeIdx, -1, onActiveIdxChange),
    _useUncontrolled2 = _slicedToArray(_useUncontrolled, 2),
    actIdx = _useUncontrolled2[0],
    setActIdx = _useUncontrolled2[1];
  var ctxVal = useMemo(function () {
    return {
      isRemovable: isRemovable,
      uploadImage: uploadImage,
      onRemove: removeHandle,
      activeIdx: actIdx,
      onActiveIdxChange: setActIdx,
      triggerMode: triggerMode,
      value: value,
      count: count
    };
  }, [isRemovable, uploadImage, actIdx, setActIdx, triggerMode, count, value]);
  return React.createElement(SingleImageUploadGroupContext.Provider, {
    value: ctxVal
  }, React.createElement(ImagePanel, {
    ref: ref,
    apiConfig: apiConfig,
    cropSizeConfig: validCropSize,
    value: value,
    onValueChange: onValueChange
  }, React.createElement(SortableList, {
    className: "".concat(cls, "-image-list"),
    helperClass: "".concat(cls, "-helper-class"),
    axis: "xy",
    distance: 10,
    items: list,
    onSortEnd: onSortEnd
  })));
};